import { DEL, GET, POST, PUT, UploadPost, setAuthToken } from "../config/ApiConfig";
import { decryptData, encryptData } from "../helpers/EncryptHelper";
import { apiError } from "../helpers/ErrorHelper";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import dateFormat from "dateformat";
import config from "../config/config";
import { cacheToStorage } from "../helpers/GeneralHelper";

const module = {
    endPoint: '/users',
    key: 'users'
};

//#region CRUD

export const create = async (user_token, formData) => {
  setAuthToken(user_token);

  return await POST(`${module.endPoint}/add-user`, formData);
};

export const update = async (user_token, id, formData) => {
  setAuthToken(user_token);

  return await PUT(`${module.endPoint}/update-user/${id}`, formData);
};

export const remove = async (user_token, id) => {
  setAuthToken(user_token);

  return await DEL(`${module.endPoint}/delete_user/${id}`);
};

export const saveToStorage = (data, pagination) => {
  if (! config.isCache) {
    return;
  }
  
  let currentTime = dateFormat("", "dd mmm yyyy HH:MM:ss");

  const dataToStore = {
    [module.key]: data,
    pagination: pagination,
    time: currentTime,
  };
  localStorage.setItem(module.key, encryptData(dataToStore));

  localStorage.removeItem('managers');
};

export const getData = async (param = {}) => {
  let user_token = param.user_token;

  if (! user_token) {
    return [];
  }

  let page = param.page ? param.page : 1;
  let limit = param.limit ? param.limit : 10;

  const useListCache =
    page == 1 &&
    limit == 10 &&
    localStorage.getItem(module.key) &&
    !(param.search && String(param.search).trim() !== "") &&
    Number(param.isTrash) !== 1 &&
    param.user_is_active === undefined;

  if (useListCache) {
    return decryptData(localStorage.getItem(module.key));
  }

  delete param.page;
  delete param.limit;
  let query = new URLSearchParams(param).toString();

  try {
    setAuthToken(user_token);

    const url = `${module.endPoint}/user-list?page=${page}&limit=${limit}&${query}`;
    const response = await GET(url);
    if (! response.status) { 
        toast.error(response.message);
    }

    if (
      page == 1 &&
      limit == 10 &&
      !(param.search && String(param.search).trim() !== "") &&
      Number(param.isTrash) !== 1 &&
      param.user_is_active === undefined
    ) {
      saveToStorage(response.data[module.key], response.data.pagination);
    }

    return response.data;

  } catch (error) {
      apiError(error);

      console.log(error);
  }
};

export const getSessionLogs = async (param = {}) => {
  const user_token = param.user_token;
  if (!user_token) {
    return { session_logs: [], pagination: { total_rows: 0, page: 1, limit: 25 } };
  }

  const page = param.page ? param.page : 1;
  const limit = param.limit ? param.limit : 25;
  const queryParams = { ...param };
  delete queryParams.user_token;
  delete queryParams.page;
  delete queryParams.limit;
  const query = new URLSearchParams(
    Object.fromEntries(
      Object.entries(queryParams).filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          value !== "" &&
          String(value) !== "undefined"
      )
    )
  ).toString();

  setAuthToken(user_token);
  const url = `${module.endPoint}/session-logs?page=${page}&limit=${limit}${query ? `&${query}` : ""}`;
  const response = await GET(url);
  if (!response.status) {
    toast.error(response.message || "Could not load session logs");
    return { session_logs: [], pagination: { total_rows: 0, page: 1, limit: 25 } };
  }
  return response.data;
};

export const backfillSessionLogs = async (user_token) => {
  if (!user_token) {
    return null;
  }
  setAuthToken(user_token);
  return await POST(`${module.endPoint}/session-logs-backfill`, {});
};

export const getLoginActivityAccess = async (user_token) => {
  if (!user_token) {
    return { allowed: false };
  }
  setAuthToken(user_token);
  const response = await GET(`${module.endPoint}/login-activity-access`);
  if (!response?.status) {
    return { allowed: false };
  }
  return response.data?.access || { allowed: false };
};

//#endregion

export const userLogin = async(formData) => {
    const endPoint = "/general/login";
    return await POST(endPoint, formData);
} 

export const userReLogin = async(user_token, formData) => {
    const endPoint = "/general/re-login";
    setAuthToken(user_token);

    return await POST(endPoint, formData);
} 

export const userLogout = async(user_token) => {
    const endPoint = "/users/logout";
    setAuthToken(user_token);

    return await GET(endPoint);
}

export const clearStorageData = () => {
    const fcm_token_enc = localStorage.getItem("fcm_token");

    const remember = localStorage.getItem("remember");
    const user_credentials_enc = localStorage.getItem("user_credentials");

    localStorage.clear();

    if (fcm_token_enc) {
        localStorage.setItem("fcm_token", fcm_token_enc);
    }

    if (remember && user_credentials_enc) {
        localStorage.setItem("remember", remember);
        localStorage.setItem("user_credentials", user_credentials_enc);
    }
}

export const saveUserCredentialToStorage = (user_email, user_password) =>{

    const credentials = {user_email, user_password};

    localStorage.setItem('remember', true);
    localStorage.setItem("user_credentials", encryptData(credentials));
}

export const removeUserCredentialFromStorage = () =>{
    localStorage.removeItem('remember');
    localStorage.removeItem("user_credentials");
}

export const getUserCredentialsFromStorage = () => {
    let credentials;
 
    if (localStorage.getItem("user_credentials")) {
        credentials = decryptData(localStorage.getItem("user_credentials"));
    } else {
        credentials = {user_email: '', user_password: ''};
    }
 
    return credentials;
}

export const userForgotPassword = async(formData) => {
    const endPoint = "/general/forgot-password";
    return await POST(endPoint, formData);
} 

export const userResetPassword = async(formData) => {
    const endPoint = "/general/reset-password";
    return await POST(endPoint, formData);
} 

export const editFCMToken = async(user_token, formData) => {

    const endPoint = `/users/send-fcm-token`;
    setAuthToken(user_token);

    return await PUT(endPoint, formData);
}; 

export const saveUserDataToStorage = (user_data) => {
    localStorage.setItem("worktracker_login", true);

    user_data.urole_permission = JSON.parse(user_data.urole_permission);
    localStorage.setItem('user_data', encryptData(user_data));
} 

export const saveUsersToStorage = (users, pagination) => {
    if (! config.isCache) {
      return;
    }
    
    let currentTime = dateFormat('', "dd mmm yyyy HH:MM:ss");

    const dataToStore = {
        users: users,
        pagination: pagination,
        time:currentTime
    };

    localStorage.setItem("users", encryptData(dataToStore));
} 

export const getUserProfile = async(param = {}) => {

    let user_token = param.user_token;
    let user_unique_id = param.user_unique_id;

    try {
        const endPoint = `/users/profile/${user_unique_id}`;
        const url = `${endPoint}`;
    
        setAuthToken(user_token);
        
        const response = await GET(url);

        if (response.status) {
            return response.data;
        }    
        else{
            toast.error(response.message);
        }
        
    } catch (error) {
        apiError(error);
    }
};

// export const createRole = async(user_token, formData) => {

//     const endPoint = "/users/add-user-role";
//     setAuthToken(user_token);

//     return await POST(endPoint, formData);
// } 

// export const editRole = async(user_token, _urole_id, formData) => {

//     const endPoint = `/users/update-user-role/${_urole_id}`;
//     setAuthToken(user_token);

//     return await PUT(endPoint, formData);
// }; 

// export const editRolePermissions = async(user_token, _urole_id, formData) => {

//     const endPoint = `/users/update-user-role-permissions/${_urole_id}`;
//     setAuthToken(user_token);

//     return await PUT(endPoint, formData);
// };

// export const editRoleMembers = async(user_token, _urole_id, formData) => {

//     const endPoint = `/users/update-user-role-members/${_urole_id}`;
//     setAuthToken(user_token);

//     return await PUT(endPoint, formData);
// };

// export const removeRole = async(user_token, _urole_id) => {

//     const endPoint = `/users/delete-user-role/${_urole_id}`;
//     setAuthToken(user_token);

//     return await DEL(endPoint);
// } 

export const saveRolesToStorage = (user_roles) => {
    if (! config.isCache) {
        return;
    }

    let currentTime = dateFormat('', "dd mmm yyyy HH:MM:ss");

    const dataToStore = {
        user_roles: user_roles,
        time:currentTime
    };

    localStorage.setItem("user_roles", encryptData(dataToStore));
} 

export const getUserRoles = async(param = {}) => {

    let user_token = param.user_token;

    if (!user_token) {
        return [];
    }

    if (config.isCache && localStorage.getItem('user_roles')) {

        const saved_roles_obj = decryptData(localStorage.getItem('user_roles'));

        return saved_roles_obj;
    }    
    else{
        try {
            const endPoint = `/users/user-role-list`;
            const url = `${endPoint}`;
        
            setAuthToken(user_token);
            
            const response = await GET(url);
    
            if (response.status) {
                saveRolesToStorage(response.data.user_roles);

                return response.data;
            }

            toast.error(response.message);
        } catch (error) {
            apiError(error);
        }
    }
};

export const updateProfile = async(user_token, user_unique_id, formData) => {

    const endPoint = `/users/update-profile/${user_unique_id}`;
    setAuthToken(user_token);

    return await PUT(endPoint, formData);
}; 

// export const updateUserJobInfo = async(user_token, user_unique_id, formData) => {

//     const endPoint = `/users/update-job-info/${user_unique_id}`;
//     setAuthToken(user_token);

//     return await PUT(endPoint, formData);
// }; 

// export const updateUserSocialNetworks = async(user_token, user_unique_id, formData) => {

//     const endPoint = `/users/update-social-networks/${user_unique_id}`;
//     setAuthToken(user_token);

//     return await PUT(endPoint, formData);
// }; 

// export const updateUserAccountInfo = async(user_token, user_unique_id, formData) => {

//     const endPoint = `/users/update-account-info/${user_unique_id}`;
//     setAuthToken(user_token);

//     return await PUT(endPoint, formData);
// }; 

// export const updateUserBankDetails = async(user_token, user_unique_id, formData) => {

//     const endPoint = `/users/update-bank-details/${user_unique_id}`;
//     setAuthToken(user_token);

//     return await PUT(endPoint, formData);
// }; 


export const updateMyPassword = async(user_token, formData) => {

    const endPoint = `/users/change-password`;
    setAuthToken(user_token);

    return await PUT(endPoint, formData);
}; 


export const updateMyPicture = async(user_token, formData) => {

    const endPoint = `/users/update-profile-picture`;
    setAuthToken(user_token);

    return await UploadPost(endPoint, formData);
};


export const removeUser = async(user_token, user_unique_id) => {

    const endPoint = `/users/delete-user/${user_unique_id}`;
    setAuthToken(user_token);

    return await DEL(endPoint);
}

export const getManagersExceptMe = async(param = {}) => {
    let user_token = param.user_token;
    if (! user_token) {
        return [];
    }

    if (config.isCache && localStorage.getItem('managers_except_me')) {
        return decryptData(localStorage.getItem('managers_except_me'));
    }

    try {
        const endPoint = `/users/get-managers-except-me`;    
        setAuthToken(user_token);
        
        const response = await GET(endPoint);
        if (response.status) {
            cacheToStorage('managers_except_me', response.data.managers);
            return response.data;
        }

        toast.error(response.message);
        
    } catch (error) {
        apiError(error);
    }
};

export const getManagers = async(param = {}) => {
    let user_token = param.user_token;
    if (! user_token) {
        return [];
    }

    if (config.isCache && localStorage.getItem('managers')) {
        return decryptData(localStorage.getItem('managers'));
    }

    try {
        const endPoint = `/users/get-managers`;    
        setAuthToken(user_token);
        
        const response = await GET(endPoint);
        if (response.status) {
            cacheToStorage('managers', response.data.managers);
            return response.data;
        }

        toast.error(response.message);
        
    } catch (error) {
        apiError(error);
    }
};

export const getRecruiters = async(param = {}) => {
    let user_token = param.user_token;
    if (! user_token) {
        return [];
    }

    if (config.isCache && localStorage.getItem('recruiters')) {
        return decryptData(localStorage.getItem('recruiters'));
    }

    try {
        const endPoint = param.user_manager_id  ? `/users/get-recruiters/${param.user_manager_id }` : `/users/get-all-recruiters`;    
        setAuthToken(user_token);
        
        const response = await GET(endPoint);
        if (response.status) {
            cacheToStorage('recruiters', response.data.recruiters);
            return response.data;
        }

        toast.error(response.message);
        
    } catch (error) {
        apiError(error);
    }
};